import type { FC } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Card from 'components/Card';
import { RouteTypes } from 'RouteTypes';
import TextUnderCard from 'components/TextUnderCard';
import { desktopBreakpoint } from 'breakpoints';
import { useWindowSize } from 'hooks/useWindowSize';

import TrimmedCardText from 'components/TrimmedCard/TrimmedCardText';
import {
  SideText,
  SideTextCapitalized,
  StyledDescriptionContainer,
  StyledTrDescribe,
  StyledTrTitle,
} from 'components/TrimmedCard/styled';

import type { IRewardSection } from './types';
import {
  RewardWrapper,
  RewardHeadline,
  RewardDescription,
  RewardDate,
  RewardCards,
  CardDescriptionTitle,
  CardDescriptionText,
  ItemWrapper,
  CardWrapper,
  UnrevealedCard,
  CardTextReward,
  UnrevealedCardContainer,
  UnrevealedCardScoreText,
  UnreaveledCardScore,
} from './styled';
import { numberWithCommas } from '../../helpers/TextHelpers';

const RewardSection: FC<IRewardSection> = ({
  collectionId,
  collection,
  cards,
  rewardDate,
  isPreview,
  textForCards,
}) => {
  const { t } = useTranslation();
  const width = useWindowSize();
  const isMobile = width < desktopBreakpoint;
  const isUnrevealed = rewardDate && (dayjs().diff(rewardDate) < 0);
  const TextUnderCardComponent = isMobile ? TrimmedCardText : TextUnderCard;
  const revealDateStr = rewardDate?.format('MMMM D');
  return (
    <RewardWrapper data-test-id="reward_wrapper" $isPreview={isPreview}>
      <RewardHeadline
        data-test-id="reward_headline"
        font="secondary"
        textColor="primary"
        size="header2"
        lSpacing={2}
        uppercase
        as="h2"
      >
        {t('collection.reward.title')}
      </RewardHeadline>

      <RewardDescription data-test-id="reward_description">
        {cards.length > 1 ? t('collection.reward.description') : t('collection.reward.oneCardDescription')}
      </RewardDescription>

      <RewardDate data-test-id="reward_date">
        {t('collection.reward.date', { rewardDate: rewardDate?.format('MMMM D, YYYY') })}
      </RewardDate>

      <RewardCards data-test-id="reward_cards">
        {(cards || []).map((card, i) => (
          <ItemWrapper key={card.rarity}>
            <CardWrapper
              data-test-id="card_wrapper"
              data-date-attribute={revealDateStr}
              $bottom={isUnrevealed && !!card.collector_score_min && !!card.collector_score_max}
            >
              {(!isUnrevealed || isPreview) ? (
                <Card
                  cardUrl={isPreview ? '' : RouteTypes.Card
                    .replace(':username', collection.creator.username || '')
                    .replace(':collectionId', collectionId || '')
                    .replace(':cardId', card.id || '')
                    .replace(':edition?', '')}
                  card={{
                    media: card.media,
                    title: card.title,
                    rarity: card.rarity,
                    qrCodeUrl: card.qrcode,
                    collection: collection.title,
                    author: collection.creator.name,
                    totalEditions: card.editions,
                    cardId: card.id,
                    'data-test-id': 'reward_card',
                    'data-date-attribute': revealDateStr,
                  }}
                  collectionId={collectionId}
                  isFlowFest={false}
                  isVideoEnded
                  isTrimmedCard={isPreview || isMobile}
                />
              ) : (
                <UnrevealedCardContainer>
                  <UnrevealedCard
                    data-test-id={`rarity_card_value_${card.rarity}`}
                    data-rarity-attribute={card.rarity}
                    rarity={card.rarity}
                    as="div"
                  >
                    <CardTextReward>{t('collection.reward.revealDate', { rewardDate: revealDateStr })}</CardTextReward>
                  </UnrevealedCard>
                  {!isMobile && card.collector_score_min && card.collector_score_max && (
                    <UnrevealedCardScoreText>
                      Score for #1:
                      <UnreaveledCardScore>{numberWithCommas(card.collector_score_max, 0)}</UnreaveledCardScore>
                    </UnrevealedCardScoreText>
                  )}
                </UnrevealedCardContainer>
              )}
            </CardWrapper>

            {(!isUnrevealed && !isPreview) ? (
              <TextUnderCardComponent
                listing={textForCards[i]?.listing}
                price={textForCards[i]?.price}
                listingsCount={textForCards[i]?.listingsCount}
                card={card}
                hideListing={false}
              />
            ) : ((isMobile && !isPreview) && (
              <div>
                <CardDescriptionTitle>
                  {t('collection.reward.cardPlus', { cardRarity: card.rarity.slice(0, card.rarity.length - 5) })}
                </CardDescriptionTitle>
                <CardDescriptionText>
                  {t('collection.reward.revealDate', { rewardDate: revealDateStr })}
                </CardDescriptionText>
                {card.collector_score_min && card.collector_score_max && (
                  <UnrevealedCardScoreText>
                    Score for #1:
                    <UnreaveledCardScore>{numberWithCommas(card.collector_score_max, 0)}</UnreaveledCardScore>
                  </UnrevealedCardScoreText>
                )}
              </div>
            ))}

            {(isMobile && isPreview) && (
              <StyledDescriptionContainer>
                <StyledTrTitle>
                  <SideText>{card.title}</SideText>
                </StyledTrTitle>
                <StyledTrDescribe>
                  <SideTextCapitalized>
                    {t('collection.reward.cardPlus', { cardRarity: card.rarity.slice(0, card.rarity.length - 5) })}
                  </SideTextCapitalized>
                  <SideText>
                    <span>#</span>
                    /
                    <span>...</span>
                  </SideText>
                </StyledTrDescribe>
              </StyledDescriptionContainer>
            )}
          </ItemWrapper>
        ))}
      </RewardCards>
    </RewardWrapper>

  );
};

export default RewardSection;
